import { useMediaMessageSenderState } from "@/zustand/MediaMessageSenderState";

import { Button } from "@/components/primitives/button";
import { CircleX, Play } from "lucide-react";
import { Avatar, AvatarImage } from "@radix-ui/react-avatar";
import { useTranslation } from "react-i18next";
import { useFileMessageSenderState } from "@/zustand/FileMessageSenderState";
import { useEffect } from "react";
import { cn } from "@/lib/utils";
import { IS_MOBILE } from "@/lib/environmentDetector";
import { MAX_AUTOPLAY_PREVIEW_SIZE } from "@/config/constants";
import useBlobToImageURL from "@/hooks/useBlobToImageURL";

/**
 * MediaAttachmentPreview component
 *
 * Purpose: Renders a preview of media attachments (images and videos) with a discard button
 *
 * How it works:
 * 1. Gets media attachment data from global state
 * 2. Renders different previews based on media type
 * 3. Provides a discard button to remove the attachment
 *
 * Key features:
 * - Image preview using Avatar component
 * - Video preview with autoplay and muted settings
 * - Responsive sizing for mobile devices
 */
export default function MediaAttachmentPreview() {
  const { t } = useTranslation();
  const {
    newMediaAttachment,
    newMediaAttachmentUrl,
    thumbnailBlob,
    mediaAttachmentFileType,
    discardMediaAttachment,
  } = useMediaMessageSenderState();

  // Staged videos at/above MAX_AUTOPLAY_PREVIEW_SIZE don't autoplay/loop. The
  // media engine otherwise plays the whole file on repeat in the input strip —
  // for a non-faststart file that's a storm of small range reads over the blob
  // (moov at the tail + per-sample reads) before the user has even sent it.
  // They render ONE stable first-frame preview via a #t= media fragment
  // (WebKit paints nothing on a bare preload="metadata"). Deliberately NOT
  // swapped for the background-captured thumbnail: the swap showed a
  // different frame (capture seeks to 0.5s) and read as a visual glitch.
  // (MOBILE is different — see useMobileThumb below: there the captured
  // thumbnail IS the preview, because iOS won't paint a non-playing <video>.)
  const isVideo = mediaAttachmentFileType === "video";
  const isStaticPreview =
    isVideo && (newMediaAttachment?.size ?? 0) >= MAX_AUTOPLAY_PREVIEW_SIZE;
  // iOS never paints a frame for a non-playing <video>, and inline muted
  // autoplay is unreliable there (Low Power Mode / autoplay refusal). Both a
  // large video's #t static frame AND a small video's autoplay can leave a
  // blank dark box (issue #1103: short/small clips showed nothing on iPhone,
  // fine on macOS). So on MOBILE every staged video shows the captured
  // thumbnail image instead of relying on the <video> painting — with a
  // Play-icon placeholder for the brief interim until the background capture
  // lands. Desktop keeps the live preview (autoplay for small, single stable
  // #t frame for large).
  const useMobileThumb = IS_MOBILE && isVideo;
  const mobileThumbUrl = useBlobToImageURL(
    useMobileThumb ? thumbnailBlob : null,
  );

  const discardFileAttachment =
    useFileMessageSenderState().discardFileAttachment;

  useEffect(() => {
    if (!newMediaAttachmentUrl) {
      return;
    }
    discardFileAttachment();
  }, [newMediaAttachmentUrl, discardFileAttachment]);

  if (!newMediaAttachmentUrl) {
    return null;
  }

  return (
    <div className="flex" data-testid="media-attachment-preview">
      <div className="relative my-1 flex min-w-0 select-none items-center gap-2 rounded-md bg-muted/30 hover:bg-muted/50">
        {mediaAttachmentFileType === "image" ? (
          <Avatar
            aria-label={t("attachmentMenu.avatarAriaLabel")}
            className="flex min-h-0 w-full flex-1 overflow-hidden rounded-sm"
          >
            <AvatarImage
              className={cn(
                "max-h-64 max-w-64 object-cover",
                IS_MOBILE && "size-20",
              )}
              src={newMediaAttachmentUrl}
              alt={t("attachmentMenu.avatarAlt")}
            />
          </Avatar>
        ) : (
          <div className="flex min-h-0 w-full flex-1 overflow-hidden rounded-sm">
            {useMobileThumb ? (
              // Mobile: show the captured thumbnail image, never the <video>
              // (see useMobileThumb above — iOS won't reliably paint it). The
              // capture can take a moment (longer for a big recording), so show
              // an instant Play-icon placeholder in the meantime and swap to
              // the real frame once it lands.
              mobileThumbUrl ? (
                <img
                  className="size-20 rounded-sm object-cover"
                  src={mobileThumbUrl}
                  alt={t("attachmentMenu.avatarAlt")}
                />
              ) : (
                <div className="grid size-20 place-items-center rounded-sm bg-muted">
                  <Play
                    className="size-6 text-muted-foreground"
                    fill="currentColor"
                  />
                </div>
              )
            ) : (
              newMediaAttachmentUrl && (
                <video
                  className={cn(
                    "max-h-64 max-w-64 rounded-sm object-cover",
                    IS_MOBILE && "size-20",
                  )}
                  src={
                    isStaticPreview
                      ? `${newMediaAttachmentUrl}#t=0.001`
                      : newMediaAttachmentUrl
                  }
                  preload="metadata"
                  autoPlay={!isStaticPreview}
                  loop={!isStaticPreview}
                  muted
                  playsInline
                />
              )
            )}
          </div>
        )}

        <Button
          size="icon"
          variant="ghost"
          onClick={discardMediaAttachment}
          className="absolute right-1 top-1 size-5 shrink-0 rounded-full bg-muted"
        >
          <CircleX />
        </Button>
      </div>
    </div>
  );
}
